import { isoDateFromFormDate } from 'admin/lib/dates'
import { parseProductGroup } from 'admin/lib/Functions/productGroupHelper'

export const formatDeliveryProductsForSave = (
  items: any,
  timeZone: string | undefined,
  isImperial: boolean
) => {
  const newItems: any[] = []

  for (const productIncludingRawValues of items ?? []) {
    // Remove Products keys that start with 'raw_'
    const productKeysWithoutRawValues = Object.keys(
      productIncludingRawValues
    ).filter(key => !key.startsWith('raw_'))

    const product: any = {}
    productKeysWithoutRawValues.forEach(key => {
      product[key] = productIncludingRawValues[key]
    })

    product.productionTime = isoDateFromFormDate(
      product.productionTime,
      timeZone
    )
    product.usedAtTime = isoDateFromFormDate(product.usedAtTime, timeZone)

    // If isImperial reformat Height, Width, Length to Yards, Feet and Inches.
    if (isImperial) {
      product.height = []
      if (product.heightYards) {
        product.height.push({
          value: product.heightYards,
          unit: 'Yard'
        })
      }
      if (product.heightFeet) {
        product.height.push({
          value: product.heightFeet,
          unit: 'Foot'
        })
      }
      if (product.heightInches) {
        product.height.push({
          value: product.heightInches,
          unit: 'Inch'
        })
      }
      delete product.heightYards
      delete product.heightFeet
      delete product.heightInches

      product.width = []
      if (product.widthYards) {
        product.width.push({
          value: product.widthYards,
          unit: 'Yard'
        })
      }
      if (product.widthFeet) {
        product.width.push({
          value: product.widthFeet,
          unit: 'Foot'
        })
      }
      if (product.widthInches) {
        product.width.push({
          value: product.widthInches,
          unit: 'Inch'
        })
      }
      delete product.widthYards
      delete product.widthFeet
      delete product.widthInches

      product.length = []
      if (product.lengthYards) {
        product.length.push({
          value: product.lengthYards,
          unit: 'Yard'
        })
      }
      if (product.lengthFeet) {
        product.length.push({
          value: product.lengthFeet,
          unit: 'Foot'
        })
      }
      if (product.lengthInches) {
        product.length.push({
          value: product.lengthInches,
          unit: 'Inch'
        })
      }
      delete product.lengthYards
      delete product.lengthFeet
      delete product.lengthInches
    } else {
      // Not Imperial
      if (product.height) {
        product.height = [
          {
            value: product.height,
            unit: product.heightUoM ? product.heightUoM : 'Millimeter'
          }
        ]
      } else {
        product.height = []
      }

      if (product.width) {
        product.width = [
          {
            value: product.width,
            unit: product.widthUoM ? product.widthUoM : 'Millimeter'
          }
        ]
      } else {
        product.width = []
      }

      if (product.length) {
        product.length = [
          {
            value: product.length,
            unit: product.lengthUoM ? product.lengthUoM : 'Millimeter'
          }
        ]
      } else {
        product.length = []
      }

      delete product.heightUoM
      delete product.widthUoM
      delete product.lengthUoM
    }

    if (product.calculatedVolume) {
      product.calculatedVolume = [
        {
          value: product.calculatedVolume,
          unit: isImperial ? 'CubicYard' : 'CubicMeter'
        }
      ]
    } else {
      product.calculatedVolume = []
    }

    if (
      product.givenVolume !== undefined &&
      product.givenVolume !== null &&
      product.givenVolume !== '' &&
      !isNaN(product.givenVolume)
    ) {
      product.givenVolume = [
        {
          value: product.givenVolume,
          unit: product.givenVolumeUoM
            ? product.givenVolumeUoM
            : isImperial
              ? 'CubicYard'
              : 'CubicMeter'
        }
      ]
    } else {
      product.givenVolume = []
    }
    delete product.givenVolumeUoM

    if (product.estimatedWeight) {
      product.estimatedWeight = [
        {
          value: product.estimatedWeight,
          unit: isImperial ? 'Pound' : 'Tonne'
        }
      ]
    } else {
      product.estimatedWeight = []
    }

    if (
      product.weight !== undefined &&
      product.weight !== null &&
      product.weight !== '' &&
      !isNaN(product.weight)
    ) {
      product.weight = [
        {
          value: product.weight,
          unit: product.weightUoM
            ? product.weightUoM
            : isImperial
              ? 'Pound'
              : 'Tonne'
        }
      ]
    } else {
      product.weight = []
    }
    delete product.weightUoM

    if (product.productGroups) {
      const parsed = parseProductGroup(product.productGroups)
      product.productGroups = parsed
        ? [{ name: parsed.name, productGroupId: parsed.productGroupId }]
        : []
    } else {
      product.productGroups = []
    }

    newItems.push(product)
  }

  return newItems
}

export const formatDeliveryRawProductsForSave = (items: any) => {
  const newItems: any[] = []

  for (const productIncludingRawValues of items ?? []) {
    // Remove Products keys that do not start with 'raw_'
    const productKeysWithRawValues = Object.keys(
      productIncludingRawValues
    ).filter(key => key.startsWith('raw_'))

    const product: any = {}
    productKeysWithRawValues.forEach(key => {
      product[key.substring(4)] = productIncludingRawValues[key]
    })
    delete product.hookFormId

    newItems.push(product)
  }

  return newItems
}

export const formatWasteProductsForSave = (items: any) => {
  const newItems: any[] = []

  for (const itemsIncludingRawValues of items ?? []) {
    // Remove item keys that start with 'raw_'
    const itemKeysWithoutRawValues = Object.keys(
      itemsIncludingRawValues
    ).filter(key => !key.startsWith('raw_'))

    const item: any = {}
    itemKeysWithoutRawValues.forEach(key => {
      item[key] = itemsIncludingRawValues[key]
    })

    if (item.volumeEstimated) {
      item.volumeEstimated = [
        {
          value: item.volumeEstimated,
          unit: 'CubicMeter'
        }
      ]
    } else {
      item.volumeEstimated = []
    }

    if (
      item.volume !== undefined &&
      item.volume !== null &&
      item.volume !== '' &&
      !isNaN(item.volume)
    ) {
      item.volume = [
        {
          value: item.volume,
          unit: item.volumeUoM ? item.volumeUoM : 'CubicMeter'
        }
      ]
    } else {
      item.volume = []
    }
    delete item.volumeUoM

    if (item.weightEstimated) {
      item.weightEstimated = [
        {
          value: item.weightEstimated,
          unit: 'Tonne'
        }
      ]
    } else {
      item.weightEstimated = []
    }

    if (
      item.weight !== undefined &&
      item.weight !== null &&
      item.weight !== '' &&
      !isNaN(item.weight)
    ) {
      item.weight = [
        {
          value: item.weight,
          unit: item.weightUoM ? item.weightUoM : 'Tonne'
        }
      ]
    } else {
      item.weight = []
    }
    delete item.weightUoM

    newItems.push(item)
  }

  return newItems
}

export const formatWasteRawProductsForSave = (items: any) => {
  const newItems: any[] = []

  for (const itemsIncludingRawValues of items ?? []) {
    // Remove item keys that do not start with 'raw_'
    const itemKeysWithRawValues = Object.keys(itemsIncludingRawValues).filter(
      key => key.startsWith('raw_')
    )

    const item: any = {}
    itemKeysWithRawValues.forEach(key => {
      item[key.substring(4)] = itemsIncludingRawValues[key]
    })
    delete item.hookFormId

    newItems.push(item)
  }

  return newItems
}
